
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { BlockComponent } from "@components/container-block/block.component";
import { TableComponent } from "@components/table/table.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ButtonComponent } from "~app/common/components/button/button.component";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { DateUtils } from "~app/common/utils/date-utils";
import { environment } from "~environments/environment";
import { SchemalaggningListTexts } from "../schemalaggning-list/schemalaggning-list.texts";
import { LastEntriesForJobbtypViewModel, SchemalaggningDetailViewModel } from "../schemalaggning.models";
import { SchemalaggningService } from "../schemalaggning.service";
@Component({
    selector: "rask-schemalaggning-details",
    templateUrl: "./schemalaggning-details.component.html",
    styleUrls: ["./schemalaggning-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        TableComponent,
        ButtonComponent
    ]
})
export class SchemalaggningDetailsComponent implements OnInit {

    details: SchemalaggningDetailViewModel[];
    jobblist: LastEntriesForJobbtypViewModel[];
    antalJobbsNotProcessed: number;
    tableColumns: RaskColumn[];
    tableColumnsJobbList: RaskColumn[];
    texts = SchemalaggningListTexts.texts;
    data: string;

    private readonly schemalaggningService = inject(SchemalaggningService);
    private readonly busyService = inject(BusyService);
    private readonly toastService = inject(ToastService);
    private readonly modal = inject(NgbActiveModal);

    constructor() {
        this.schemalaggningService.currentHistory$.subscribe(x => {
            this.details = x.map(y => new SchemalaggningDetailViewModel(y));
        });

        this.schemalaggningService.jobbList$.subscribe(x => {
            this.jobblist = x.map(y => new LastEntriesForJobbtypViewModel(y));
        });
    }

    ngOnInit(): void {
        this.schemalaggningService.checkJobbNotProcessed(this.data)
            .then((result) => {
                this.antalJobbsNotProcessed = result;
            });
        this.schemalaggningService.getHistory(this.data);
        this.initColumns();
        this.schemalaggningService.getJobbList(this.data);
        this.initColumnsJobEntries();
    }

    dismiss() {
        this.modal.close(ConfirmDialogResponse.cancel);
    }

    private initColumnsJobEntries(): void {
        this.tableColumnsJobbList = [
            {
                editable: false,
                headerName: "JobbId",
                field: "jobbId",
                filter: true,
                onClick: (jobbEntry: LastEntriesForJobbtypViewModel) => this.openApplicationInsights(jobbEntry),
                sortable: false,
                maxWidth: 95,
                type: "link"
            },
            {
                editable: false,
                headerName: "Status",
                field: "status",
                filter: true,
                sortable: true,
                minWidth: 95,
                maxWidth: 95,
                type: "span"
            },
            {
                editable: false,
                headerName: "Start",
                field: "start",
                filter: true,
                sortable: true,
                maxWidth: 150,
                type: "span"
            },
            {
                editable: false,
                headerName: "Slut",
                field: "slut",
                filter: true,
                sortable: true,
                maxWidth: 150,
                type: "span"
            },
            {
                editable: false,
                headerName: "Meddelande",
                field: "meddelande",
                filter: true,
                sortable: true,
                minWidth: 150,
                type: "span",
                cellStyle: { "white-space": "normal !important" }
            },
            {
                editable: false,
                field: "Trigga",
                minWidth: 100,
                maxWidth: 120,
                onClick: (jobbEntry: LastEntriesForJobbtypViewModel) => this.runJobbAgain(jobbEntry, "ForsokIgen"),
                filter: false,
                type: "button",
                text: this.texts.runjobbagain
            },
            {
                editable: false,
                field: "Ignorera",
                minWidth: 100,
                maxWidth: 120,
                onClick: (jobbEntry: LastEntriesForJobbtypViewModel) => this.runJobbAgain(jobbEntry, "Ignorerad"),
                filter: false,
                type: "button",
                text: this.texts.setJobbToIgnore
            }
        ] as any[] as RaskColumn[];
    }

    private initColumns(): void {
        this.tableColumns = [
            {
                editable: false,
                headerName: this.texts.status,
                field: "statuskod",
                maxWidth: 100,
                filter: true,
                sortable: true,
                type: "span",
                cellClassRules: {
                    "cell-ok": (params: any) => params.value === "Ok",
                    "cell-error": (params: any) => params.value === "Error"
                }
            },
            {
                editable: false,
                headerName: this.texts.start,
                field: "start",
                filter: true,
                sortable: true,
                maxWidth: 160,
                type: "span",
                tooltipGenerator: (row, value) => row.start
            },
            {
                editable: false,
                headerName: this.texts.slut,
                field: "slut",
                filter: true,
                sortable: true,
                maxWidth: 160,
                type: "span",
                tooltipGenerator: (row, value) => row.slut
            },
            {
                editable: false,
                headerName: this.texts.varaktighet,
                field: "varaktighet",
                maxWidth: 120,
                filter: true,
                sortable: true,
                type: "span"
            },
            {
                editable: false,
                headerName: this.texts.resultat,
                field: "resultat",
                minWidth: 580,
                maxWidth: 580,
                filter: true,
                sortable: true,
                type: "span",
                cellStyle: { "white-space": "normal !important" },
                tooltipGenerator: (row, value) => row.resultat
            }
        ] as any[] as RaskColumn[];
    }

    private openApplicationInsights(jobbEntry: LastEntriesForJobbtypViewModel): void {
        const from = moment(jobbEntry.start).add(-1, "minute").toDate();
        const to = moment(jobbEntry.slut).add(1, "minute").toDate();

        let url = `${environment.apiV2}/maintenance/ai/jobberror?`;
        url += `jobbId=${jobbEntry.jobbId.toString()}`;
        url += `&jobbType=${jobbEntry.jobbtyp}`;
        url += `&startTime=${DateUtils.formatDateString(from, "YYYY-MM-DDTHH:mm:ss.sss")}`;
        url += `&stopTime=${DateUtils.formatDateString(to, "YYYY-MM-DDTHH:mm:ss.sss")}`;

        window.open(url, "_blank", "noreferrer");
    }

    private runJobbAgain(jobbEntry: LastEntriesForJobbtypViewModel, jobbstatus: string): void {
        if (!this.isValidJobbtyp(jobbEntry.jobbtyp)) {
            this.toastService.add(`Kan inte påverka denna typ av jobb => ${jobbEntry.jobbtyp}`);
            return;
        }

        if (jobbEntry.status === "Ny") {
            this.toastService.add(`Kan inte påverka när status är => ${jobbEntry.status}`);
            return;
        }

        const worker = this.busyService.addWork(this.schemalaggningService.modifyJobbStatus(jobbEntry.jobbtyp, jobbstatus));
        worker.subscribe(result => {
            this.toastService.add(`Ändrar jobb status till ${jobbstatus}`);
            if (result) {
                this.toastService.add(`JobbId: ${result.id}, ${result.message}`);
            }
        });
    }

    private isValidJobbtyp(jobbnamn: string): boolean {
        switch (jobbnamn) {
            case "EnkelUtbytesberakning":
            case "NavetAtgardUppdaterad":
            case "UpdateLagersaldoForLagerplats":
            case "ArbetsorderCentrumkoordinatUpdated":
                return true;
            default:
                return false;
        }
    }
}
